import { Stack } from '@mui/material';
import { generatePath, Navigate } from 'react-router';
import { z } from 'zod';

import { PATH, url } from '../../appRouter/url.js';
import { FailedQueryAlert } from '../../components/general/ErrorAlert.js';
import {
  ContentLoading,
  EasyBreadcrumbs,
  type EasyBreadcrumbsItem,
  NoContentInfo,
} from '../../components/general/index.js';
import { useNormalizedParams } from '../../hooks/useNormalizedParams.js';
import { usePageTitle } from '../../hooks/usePageTitle.js';
import { trpc } from '../../trpc.js';
import { isTermingruppe } from '../../utils/index.js';
import { formatBuchungsnummern } from '../../utils/themaProdukte';
import { BlendedThema } from './BlendedLearning/BlendedThema.js';
import { Thema } from './Thema.js';

export const ThemaDetailsPage = () => {
  const { akaThemaId } = useNormalizedParams();

  if (akaThemaId === undefined) {
    throw new TypeError('Die Aka-Thema-Id fehlt in der URL.');
  }

  const themaQuery = trpc.thema.getThemaByAkaThemaId.useQuery(akaThemaId);

  usePageTitle(themaQuery.data && `${formatBuchungsnummern(themaQuery.data.thema)} | Thema`);

  // themaId is actually an old produktid
  // Redirect for now. When removing this, do not forget to check / adapt the urls in renderJiraIssue
  if (z.templateLiteral(['P', z.string()]).safeParse(akaThemaId).success) {
    return (
      <Navigate
        to={generatePath(PATH.THEMA.DETAILS_REDIRECT_BY_AKA_PRODUKT_ID, { akaProduktId: akaThemaId })}
        replace
      />
    );
  }
  if (themaQuery.error && themaQuery.error.data?.code !== 'NOT_FOUND') {
    return <FailedQueryAlert error={themaQuery.error} query="Thema" />;
  }

  if (themaQuery.isLoading) {
    return <ContentLoading />;
  }

  const thema = themaQuery.data?.thema;

  if (!thema) {
    return <NoContentInfo content="Kein Thema gefunden" />;
  }

  const breadcrumbs: EasyBreadcrumbsItem[] = [
    {
      href: url.root(),
      label: 'Dashboard',
    },
    {
      href: '',
      label: 'Thema',
    },
  ];

  if (isTermingruppe(thema.planungsformat)) {
    return (
      <Stack sx={{ mt: 2, mb: 45 }}>
        <EasyBreadcrumbs items={breadcrumbs} />
        <BlendedThema thema={thema} />
      </Stack>
    );
  } else {
    return (
      <Stack sx={{ mt: 2, mb: 45 }}>
        <EasyBreadcrumbs items={breadcrumbs} />
        <Thema thema={thema} />
      </Stack>
    );
  }
};
